import React, { useState, useEffect } from 'react';
import { Plus, Image, Edit, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, X, Play } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { ImageUploader, VideoUploader } from '../../components/admin';
import { toast } from 'sonner';

const positionLabels = {
  home_hero: 'Hero Principal',
  flash_sale: 'Flash Sale (Hero)',
  home_secondary: 'Secundario Home',
  category_header: 'Cabecera Categoría',
  promo_strip: 'Franja Promocional'
};

const AdminBanners = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '', 
    subtitle: '', 
    image_url: '', 
    mobile_image_url: '',
    media_url: '',
    media_type: 'image',
    link_url: '', 
    position: 'home_hero', 
    order: 0, 
    is_active: true,
    hide_overlay_text: false
  });

  useEffect(() => { loadBanners(); }, []);

  const loadBanners = async () => {
    try {
      setLoading(true);
      const res = await fetchWithAuth('/api/admin/banners');
      if (res.ok) { 
        const data = await res.json(); 
        setBanners(data.banners); 
      }
    } catch (error) { 
      console.error(error); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate based on media type
    if (formData.media_type === 'image' && !formData.image_url) {
      toast.error('Por favor sube una imagen');
      return;
    }
    if (formData.media_type === 'video' && !formData.media_url) {
      toast.error('Por favor ingresa la URL del video');
      return;
    }
    
    try {
      const res = editingBanner
        ? await fetchWithAuth(`/api/admin/banners/${editingBanner.id}`, { method: 'PUT', body: formData })
        : await fetchWithAuth('/api/admin/banners', { method: 'POST', body: formData });
      if (res.ok) {
        toast.success(editingBanner ? 'Banner actualizado' : 'Banner creado');
        setShowModal(false);
        setEditingBanner(null);
        resetForm();
        loadBanners();
      } else { 
        const err = await res.json(); 
        toast.error(err.detail || 'Error'); 
      }
    } catch (error) { 
      toast.error('Error'); 
    }
  };

  const deleteBanner = async (id) => {
    if (!window.confirm('¿Eliminar este banner?')) return;
    try {
      const res = await fetchWithAuth(`/api/admin/banners/${id}`, { method: 'DELETE' });
      if (res.ok) { 
        toast.success('Banner eliminado'); 
        loadBanners(); 
      }
    } catch (error) { 
      toast.error('Error'); 
    }
  };

  const toggleBannerVisibility = async (banner) => {
    try {
      const res = await fetchWithAuth(`/api/admin/banners/${banner.id}`, { 
        method: 'PUT', 
        body: { is_active: !banner.is_active } 
      });
      if (res.ok) { 
        toast.success('Visibilidad actualizada'); 
        loadBanners(); 
      }
    } catch (error) { 
      toast.error('Error'); 
    }
  };

  const updateOrder = async (banner, direction) => {
    const newOrder = direction === 'up' ? banner.order - 1 : banner.order + 1;
    try {
      const res = await fetchWithAuth(`/api/admin/banners/${banner.id}`, { 
        method: 'PUT', 
        body: { order: newOrder } 
      });
      if (res.ok) { loadBanners(); }
    } catch (error) { 
      toast.error('Error'); 
    }
  };

  const openEditModal = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title, 
      subtitle: banner.subtitle || '', 
      image_url: banner.image_url || '', 
      mobile_image_url: banner.mobile_image_url || '',
      media_url: banner.media_url || '',
      media_type: banner.media_type || 'image',
      link_url: banner.link_url || '', 
      position: banner.position, 
      order: banner.order, 
      is_active: banner.is_active,
      hide_overlay_text: banner.hide_overlay_text || false
    });
    setShowModal(true);
  };

  const resetForm = () => { 
    setFormData({ 
      title: '', 
      subtitle: '', 
      image_url: '', 
      mobile_image_url: '',
      media_url: '',
      media_type: 'image',
      link_url: '', 
      position: 'home_hero', 
      order: 0, 
      is_active: true,
      hide_overlay_text: false
    }); 
  };

  const groupedBanners = banners.reduce((acc, b) => {
    if (!acc[b.position]) acc[b.position] = [];
    acc[b.position].push(b);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="admin-banners">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-white">Banners</h2>
          <p className="text-gray-400 text-sm">{banners.length} banners</p>
        </div>
        <button 
          onClick={() => { resetForm(); setEditingBanner(null); setShowModal(true); }} 
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg"
        >
          <Plus size={18} />Nuevo Banner
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      ) : banners.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center text-gray-400">
          <Image className="mx-auto mb-3" size={40} />
          <p>No hay banners</p>
          <p className="text-sm mt-2">Crea tu primer banner para mostrar en el sitio</p>
        </div>
      ) : (
        Object.entries(groupedBanners).map(([position, positionBanners]) => (
          <div key={position} className="bg-gray-800 rounded-xl p-6">
            <h3 className="text-lg font-medium text-white mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-amber-500"></span>
              {positionLabels[position] || position}
              <span className="text-sm text-gray-500 font-normal">({positionBanners.length})</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {positionBanners.sort((a, b) => a.order - b.order).map(banner => (
                <div 
                  key={banner.id} 
                  className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                    banner.is_active ? 'border-amber-500/50 hover:border-amber-500' : 'border-gray-700 opacity-60'
                  }`}
                >
                  <div className="aspect-video bg-gray-700">
                    {banner.media_type === 'video' ? (
                      <video 
                        src={banner.media_url || banner.video_url} 
                        className="w-full h-full object-cover"
                        autoPlay
                        loop
                        muted
                        playsInline
                      />
                    ) : (
                      <img 
                        src={banner.image_url || banner.media_url} 
                        alt={banner.title} 
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/400x225?text=Sin+Imagen'; }}
                      />
                    )}
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent flex flex-col justify-end p-4">
                    <div className="flex items-center gap-2 mb-1">
                      {banner.media_type === 'video' && (
                        <span className="bg-purple-500/80 px-1.5 py-0.5 rounded text-[10px] text-white flex items-center gap-1">
                          <Play size={10} /> Video
                        </span>
                      )}
                    </div>
                    <h4 className="text-white font-medium truncate">{banner.title}</h4>
                    {banner.subtitle && (
                      <p className="text-gray-300 text-sm truncate">{banner.subtitle}</p>
                    )}
                    {banner.link_url && (
                      <p className="text-amber-400 text-xs truncate mt-1">→ {banner.link_url}</p>
                    )}
                  </div>
                  {!banner.is_active && (
                    <div className="absolute top-2 left-2 bg-gray-900/80 px-2 py-1 rounded text-xs text-gray-400">
                      Oculto
                    </div>
                  )}
                  <div className="absolute top-2 right-2 flex gap-1">
                    <button 
                      onClick={() => updateOrder(banner, 'up')} 
                      className="p-1.5 bg-gray-900/80 text-gray-300 hover:text-white rounded"
                      title="Mover arriba"
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button 
                      onClick={() => updateOrder(banner, 'down')} 
                      className="p-1.5 bg-gray-900/80 text-gray-300 hover:text-white rounded"
                      title="Mover abajo"
                    >
                      <ArrowDown size={14} />
                    </button>
                    <button 
                      onClick={() => toggleBannerVisibility(banner)} 
                      className="p-1.5 bg-gray-900/80 text-gray-300 hover:text-amber-500 rounded"
                      title={banner.is_active ? 'Ocultar' : 'Mostrar'}
                    >
                      {banner.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button 
                      onClick={() => openEditModal(banner)} 
                      className="p-1.5 bg-gray-900/80 text-gray-300 hover:text-amber-500 rounded"
                      title="Editar"
                    >
                      <Edit size={14} />
                    </button>
                    <button 
                      onClick={() => deleteBanner(banner.id)} 
                      className="p-1.5 bg-gray-900/80 text-gray-300 hover:text-red-500 rounded"
                      title="Eliminar"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {/* Modal de Crear/Editar */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-gray-800 rounded-xl max-w-2xl w-full my-8 flex-shrink-0"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h3 className="text-xl font-semibold text-white">
                {editingBanner ? 'Editar Banner' : 'Nuevo Banner'}
              </h3>
              <button 
                onClick={() => setShowModal(false)} 
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Media Type Selector */}
              <div>
                <label className="block text-sm text-gray-300 mb-2">Tipo de Media *</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="media_type"
                      value="image"
                      checked={formData.media_type === 'image'}
                      onChange={() => setFormData(prev => ({ ...prev, media_type: 'image' }))}
                      className="text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-white">Imagen</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="media_type"
                      value="video"
                      checked={formData.media_type === 'video'}
                      onChange={() => setFormData(prev => ({ ...prev, media_type: 'video' }))}
                      className="text-amber-500 focus:ring-amber-500"
                    />
                    <span className="text-white">Video</span>
                  </label>
                </div>
              </div>

              {/* Image Upload - Only shown for image type */}
              {formData.media_type === 'image' && (
                <div className="space-y-4">
                  <ImageUploader
                    value={formData.image_url}
                    onChange={(url) => setFormData(prev => ({ ...prev, image_url: url }))}
                    folder="banners"
                    label="Imagen Desktop *"
                    aspectRatio="16:9"
                    previewHeight={180}
                  />
                  <ImageUploader
                    value={formData.mobile_image_url}
                    onChange={(url) => setFormData(prev => ({ ...prev, mobile_image_url: url }))}
                    folder="banners"
                    label="Imagen Móvil (opcional)"
                    aspectRatio="9:16"
                    previewHeight={180}
                  />
                  <p className="text-xs text-gray-400 -mt-2">
                    La imagen móvil se mostrará en dispositivos pequeños. Si no se sube, se usará la imagen desktop.
                  </p>
                </div>
              )}

              {/* Video Upload - Only shown for video type */}
              {formData.media_type === 'video' && (
                <VideoUploader
                  value={formData.media_url}
                  onChange={(url) => setFormData(prev => ({ ...prev, media_url: url }))}
                  folder="banners"
                  label="Video del Banner *"
                  previewHeight={200}
                />
              )}

              {/* Title & Subtitle */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Título *</label>
                  <input 
                    type="text" 
                    value={formData.title} 
                    onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))} 
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500" 
                    placeholder="Ej: Nueva Colección Primavera"
                    required 
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Subtítulo</label>
                  <input 
                    type="text" 
                    value={formData.subtitle} 
                    onChange={e => setFormData(prev => ({ ...prev, subtitle: e.target.value }))} 
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500" 
                    placeholder="Ej: Descubre las fragancias de temporada"
                  />
                </div>
              </div>

              {/* Link URL */}
              <div>
                <label className="block text-sm text-gray-300 mb-2">URL de Enlace</label>
                <input 
                  type="text" 
                  value={formData.link_url} 
                  onChange={e => setFormData(prev => ({ ...prev, link_url: e.target.value }))} 
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500" 
                  placeholder="/products o https://..."
                />
                <p className="text-xs text-gray-500 mt-1">Ruta interna (ej: /products) o URL completa</p>
              </div>

              {/* Position & Order */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Posición *</label>
                  <select
                    value={formData.position}
                    onChange={e => setFormData(prev => ({
                      ...prev,
                      position: e.target.value,
                      // Los banners de Flash Sale son gráficos ya diseñados con su propio
                      // texto/CTA — por defecto no queremos el texto genérico del Hero encima.
                      hide_overlay_text: e.target.value === 'flash_sale' ? true : prev.hide_overlay_text
                    }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500"
                  >
                    {Object.entries(positionLabels).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-2">Orden</label>
                  <input 
                    type="number" 
                    value={formData.order} 
                    onChange={e => setFormData(prev => ({ ...prev, order: Number(e.target.value) }))} 
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-amber-500" 
                  />
                  <p className="text-xs text-gray-500 mt-1">Menor número = aparece primero</p>
                </div>
              </div>

              {/* Active Toggle */}
              <label className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={formData.is_active} 
                  onChange={e => setFormData(prev => ({ ...prev, is_active: e.target.checked }))} 
                  className="w-5 h-5 accent-amber-500" 
                />
                <div>
                  <span className="text-sm text-white font-medium">Banner activo</span>
                  <p className="text-xs text-gray-400">El banner será visible en el sitio</p>
                </div>
              </label>

              {/* Hide Overlay Text Toggle - Solo aplica a banners que viven en el Hero */}
              {(formData.position === 'home_hero' || formData.position === 'flash_sale') && (
                <label className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.hide_overlay_text}
                    onChange={e => setFormData(prev => ({ ...prev, hide_overlay_text: e.target.checked }))}
                    className="w-5 h-5 accent-amber-500"
                  />
                  <div>
                    <span className="text-sm text-white font-medium">Ocultar texto superpuesto</span>
                    <p className="text-xs text-gray-400">No mostrar "Fragancias de Lujo" ni el botón sobre este banner</p>
                  </div>
                </label>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)} 
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-600"
                >
                  {editingBanner ? 'Guardar Cambios' : 'Crear Banner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBanners;
